import { UseFormReturn, FieldErrors } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { NewNonCustomerFormData } from "./schemas";
import { useEffect, useState } from "react";
import parsePhoneNumber, { AsYouType as PhoneNumberFormatterAsYouType } from 'libphonenumber-js';

interface NewNonCustomerFormProps {
  form: UseFormReturn<NewNonCustomerFormData>;
  errors: FieldErrors<NewNonCustomerFormData>;
  isPreview?: boolean;
}

export function NewNonCustomerForm({ form, errors, isPreview = false }: NewNonCustomerFormProps) {
  const { register, setValue, watch } = form;

  const watchedValues = {
    corn_cfr_check: watch("corn_cfr_check"),
    canola_cfr_check: watch("canola_cfr_check"),
    email: watch("email"),
    mailing_address_line_one: watch("mailing_address_line_one"),
    mailing_address_line_two: watch("mailing_address_line_two"),
    mailing_city: watch("mailing_city"),
    mailing_state: watch("mailing_state"),
    mailing_zipcode: watch("mailing_zipcode"),
    mailing_country: watch("mailing_country"),
    mailing_county: watch("mailing_county"),
    customer_address_line_one: watch("customer_address_line_one"),
    customer_address_line_two: watch("customer_address_line_two"),
    customer_city: watch("customer_city"),
    customer_state: watch("customer_state"),
    customer_zipcode: watch("customer_zipcode"),
    customer_country: watch("customer_country"),
    customer_county: watch("customer_county"),
    bank_institution_name: watch("bank_institution_name"),
    banker_name: watch("banker_name"),
    banker_phone: watch("banker_phone"),
    banker_email: watch("banker_email"),
    has_lienholder: watch("has_lienholder"),
    ach_form_country_type: watch("ach_form_country_type"),
  };

  const [addressesSame, setAddressesSame] = useState(false);
  
  // Banker phone number formatting state
  const [bankerPhoneInput, setBankerPhoneInput] = useState("");
  
  // Initialize from form data
  useEffect(() => {
    if (watchedValues.banker_phone) {
      setBankerPhoneInput(watchedValues.banker_phone);
    }
  }, []);

  const handleNumberInput = (field: keyof Pick<NewNonCustomerFormData, "number_of_partners" | "number_of_bols_needed">) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setValue(field, value === "" ? 0 : parseInt(value, 10));
    }
  };

  // Handle address sync when checkbox is checked
  const handleAddressesSameChange = (checked: boolean) => {
    setAddressesSame(checked);
    if (checked) {
      setValue("customer_address_line_one", watchedValues.mailing_address_line_one, { shouldDirty: true });
      setValue("customer_address_line_two", watchedValues.mailing_address_line_two, { shouldDirty: true });
      setValue("customer_city", watchedValues.mailing_city, { shouldDirty: true });
      setValue("customer_state", watchedValues.mailing_state, { shouldDirty: true });
      setValue("customer_zipcode", watchedValues.mailing_zipcode, { shouldDirty: true });
      setValue("customer_country", watchedValues.mailing_country, { shouldDirty: true });
      setValue("customer_county", watchedValues.mailing_county, { shouldDirty: true });
    }
  };

  // Update customer address when mailing address changes and checkbox is checked
  useEffect(() => {
    if (addressesSame) {
      setValue("customer_address_line_one", watchedValues.mailing_address_line_one, { shouldDirty: true });
      setValue("customer_address_line_two", watchedValues.mailing_address_line_two, { shouldDirty: true });
      setValue("customer_city", watchedValues.mailing_city, { shouldDirty: true });
      setValue("customer_state", watchedValues.mailing_state, { shouldDirty: true });
      setValue("customer_zipcode", watchedValues.mailing_zipcode, { shouldDirty: true });
      setValue("customer_country", watchedValues.mailing_country, { shouldDirty: true });
      setValue("customer_county", watchedValues.mailing_county, { shouldDirty: true });
    }
  }, [watchedValues.mailing_address_line_one, watchedValues.mailing_address_line_two, watchedValues.mailing_city, watchedValues.mailing_state, watchedValues.mailing_zipcode, watchedValues.mailing_country, watchedValues.mailing_county, addressesSame]);

  // Banker phone number formatting handler
  const handleBankerPhoneInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatter = new PhoneNumberFormatterAsYouType('US');
    const formattedValue = formatter.input(value);
    setBankerPhoneInput(formattedValue);
    setValue("banker_phone", formattedValue, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <div className="space-y-4">
      {/* Basic Customer Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label htmlFor="customer_name">Customer Name *</Label>
          <Input
            id="customer_name"
            {...register("customer_name")}
            placeholder="Enter customer name"
            disabled={isPreview}
            className="w-full"
          />
          {errors.customer_name && (
            <p className="text-sm text-destructive">{errors.customer_name.message}</p>
          )}
        </div>
        <div className="flex items-center space-x-2 mb-1">
          <Checkbox
            id="addresses_same"
            checked={addressesSame}
            onCheckedChange={handleAddressesSameChange}
            disabled={isPreview}
          />
          <Label htmlFor="addresses_same" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Addresses are the same
          </Label>
        </div>
      </div>

      {/* Address Information */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Mailing Address */}
          <div className="space-y-3">
            <div className="space-y-2 border border-gray-200 rounded-md p-2">
              <Label className="text-base font-semibold">Mailing Address</Label>
              <div className="space-y-1">
                <Label htmlFor="mailing_address_line_one" className="text-sm">Address Line 1</Label>
                <Input
                  id="mailing_address_line_one"
                  {...register("mailing_address_line_one")}
                  placeholder="Street address"
                  disabled={isPreview}
                  className="w-full"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mailing_address_line_two" className="text-sm">Address Line 2</Label>
                <Input
                  id="mailing_address_line_two"
                  {...register("mailing_address_line_two")}
                  placeholder="Apartment, suite, etc. (optional)"
                  disabled={isPreview}
                  className="w-full"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="mailing_city" className="text-sm">City</Label>
                  <Input
                    id="mailing_city"
                    {...register("mailing_city")}
                    placeholder="City"
                    disabled={isPreview}
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mailing_state" className="text-sm">State</Label>
                  <Input
                    id="mailing_state"
                    {...register("mailing_state")}
                    placeholder="State"
                    disabled={isPreview}
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mailing_zipcode" className="text-sm">Zipcode</Label>
                  <Input
                    id="mailing_zipcode"
                    {...register("mailing_zipcode")}
                    placeholder="Zipcode"
                    disabled={isPreview}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="flex flex-col md:grid md:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="mailing_county" className="text-sm">County</Label>
                  <Input
                    id="mailing_county"
                    {...register("mailing_county")}
                    placeholder="County"
                    disabled={isPreview}
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mailing_country" className="text-sm">Country</Label>
                  <Combobox
                    options={[
                      { value: "USA", label: "USA" },
                      { value: "CANADA", label: "CANADA" }
                    ]}
                    value={watchedValues.mailing_country || ""}
                    onValueChange={(value) => setValue("mailing_country", value as "USA" | "CANADA", { shouldDirty: true })}
                    placeholder="Select country..."
                    className="w-full"
                    popoverClassName="w-full"
                    dwimOnChange
                    isModal
                    disabled={isPreview}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Customer Address (Farm Location) */}
          <div className="space-y-3">
            <div className="space-y-2 border border-gray-200 rounded-md p-2">
              <Label className="text-base font-semibold">Farm Location</Label>
              <div className="space-y-1">
                <Label htmlFor="customer_address_line_one" className="text-sm">Address Line 1</Label>
                <Input
                  id="customer_address_line_one"
                  {...register("customer_address_line_one")}
                  placeholder="Street address"
                  disabled={isPreview || addressesSame}
                  className="w-full"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="customer_address_line_two" className="text-sm">Address Line 2</Label>
                <Input
                  id="customer_address_line_two"
                  {...register("customer_address_line_two")}
                  placeholder="Apartment, suite, etc. (optional)"
                  disabled={isPreview || addressesSame}
                  className="w-full"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="customer_city" className="text-sm">City</Label>
                  <Input
                    id="customer_city"
                    {...register("customer_city")}
                    placeholder="City"
                    disabled={isPreview || addressesSame}
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="customer_state" className="text-sm">State</Label>
                  <Input
                    id="customer_state"
                    {...register("customer_state")}
                    placeholder="State"
                    disabled={isPreview || addressesSame}
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="customer_zipcode" className="text-sm">Zipcode</Label>
                  <Input
                    id="customer_zipcode"
                    {...register("customer_zipcode")}
                    placeholder="Zipcode"
                    disabled={isPreview || addressesSame}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="flex flex-col md:grid md:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="customer_county" className="text-sm">County</Label>
                  <Input
                    id="customer_county"
                    {...register("customer_county")}
                    placeholder="County"
                    disabled={isPreview || addressesSame}
                    className="w-full"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="customer_country" className="text-sm">Country</Label>
                  <Combobox
                    options={[
                      { value: "USA", label: "USA" },
                      { value: "CANADA", label: "CANADA" }
                    ]}
                    value={watchedValues.customer_country || ""}
                    onValueChange={(value) => setValue("customer_country", value as "USA" | "CANADA", { shouldDirty: true })}
                    placeholder="Select country..."
                    className="w-full"
                    popoverClassName="w-full"
                    dwimOnChange
                    isModal
                    disabled={isPreview || addressesSame}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>



      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="space-y-1 h-full">
          <Label htmlFor="customer_contact_info">Contact Info *</Label>
          <Textarea
            id="customer_contact_info"
            {...register("customer_contact_info")}
            placeholder="Enter contact information (phone, email, etc.)"
            rows={3}
            disabled={isPreview}
            className="w-full resize-none"
          />
          {errors.customer_contact_info && (
            <p className="text-sm text-destructive">{errors.customer_contact_info.message}</p>
          )}
        </div>

        <div className="flex flex-col justify-around">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="corn_cfr_check"
              checked={watchedValues.corn_cfr_check}
              onCheckedChange={(checked) => setValue("corn_cfr_check", !!checked)}
              disabled={isPreview}
            />
            <Label htmlFor="corn_cfr_check" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Corn CFR Needed
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="canola_cfr_check"
              checked={watchedValues.canola_cfr_check}
              onCheckedChange={(checked) => setValue("canola_cfr_check", !!checked)}
              disabled={isPreview}
            />
            <Label htmlFor="canola_cfr_check" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Canola CFR Needed
            </Label>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="space-y-2">
          <Label htmlFor="number_of_partners">Number of Partners</Label>
          <Input
            id="number_of_partners"
            type="number"
            step="1"
            {...register("number_of_partners", { valueAsNumber: true })}
            placeholder="Enter number of partners"
            onChange={handleNumberInput("number_of_partners")}
            disabled={isPreview}
            className="w-full"
          />
          {errors.number_of_partners && (
            <p className="text-sm text-destructive">{errors.number_of_partners.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="number_of_bols_needed">Number of BOLs Needed</Label>
          <Input
            id="number_of_bols_needed"
            type="number"
            step="1"
            {...register("number_of_bols_needed", { valueAsNumber: true })}
            placeholder="Enter number of BOLs needed"
            onChange={handleNumberInput("number_of_bols_needed")}
            disabled={isPreview}
            className="w-full"
          />
          {errors.number_of_bols_needed && (
            <p className="text-sm text-destructive">{errors.number_of_bols_needed.message}</p>
          )}
        </div>
      </div>

      {/* Email */}
      <div className="space-y-1">
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          {...register("email")}
          placeholder="name@example.com"
          disabled={isPreview}
          className="w-full"
        />
        {errors.email && (
          <p className="text-sm text-destructive">{errors.email.message}</p>
        )}
      </div>

      {/* Bank and Lienholder Information */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="bank_institution_name">Bank Institution Name {watchedValues.has_lienholder ? "*" : ""}</Label>
            <Input
              required={!!watchedValues.has_lienholder}
              id="bank_institution_name"
              {...register("bank_institution_name")}
              placeholder="Enter bank institution name"
              disabled={isPreview}
              className="w-full"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="banker_name">Banker's Name</Label>
            <Input
              id="banker_name"
              {...register("banker_name")}
              placeholder="Enter banker's name"
              disabled={isPreview}
              className="w-full"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="banker_phone">Banker Phone {watchedValues.has_lienholder ? "*" : ""}</Label>
            <Input
              required={!!watchedValues.has_lienholder}
              id="banker_phone"
              type="tel"
              value={bankerPhoneInput}
              onChange={handleBankerPhoneInputChange}
              placeholder="(555) 123-4567"
              disabled={isPreview}
              className="w-full"
            />
            {errors.banker_phone && (
              <p className="text-sm text-destructive">{errors.banker_phone.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="banker_email">Banker Email {watchedValues.has_lienholder ? "*" : ""}</Label>
            <Input
              required={!!watchedValues.has_lienholder}
              id="banker_email"
              type="email"
              {...register("banker_email")}
              placeholder="banker@example.com"
              disabled={isPreview}
              className="w-full"
            />
            {errors.banker_email && (
              <p className="text-sm text-destructive">{errors.banker_email.message}</p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="has_lienholder"
            checked={watchedValues.has_lienholder}
            onCheckedChange={(checked) => setValue("has_lienholder", !!checked)}
            disabled={isPreview}
          />
          <Label htmlFor="has_lienholder" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Has Lienholder
          </Label>
        </div>

        {/* ACH Form Country Type */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="ach_form_country_type"
            checked={watchedValues.ach_form_country_type === "CAD"}
            onCheckedChange={(checked) => {
              setValue("ach_form_country_type", checked ? "CAD" : "USA", { shouldDirty: true, shouldValidate: true });
            }}
            disabled={isPreview}
          />
          <Label htmlFor="ach_form_country_type" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            CAD
          </Label>
        </div>
        {errors.ach_form_country_type && (
          <p className="text-sm text-destructive">{errors.ach_form_country_type.message}</p>
        )}
      </div>



      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          {...register("notes")}
          placeholder="Enter any additional notes"
          rows={4}
          disabled={isPreview}
          className="w-full resize-none"
        />
        {errors.notes && (
          <p className="text-sm text-destructive">{errors.notes.message}</p>
        )}
      </div>

      {/* Hidden Schema Version Fields */}
      <input type="hidden" {...register("schema_version")} value="v2" />
      <input type="hidden" {...register("schema_type")} value="new_non_customer_form" />
    </div>
  );
}
